import { useRef, useState } from "react";
import { Camera, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Emblem, EmblemPicker } from "./Emblem";

export function AvatarDialog({ onSaved }: { onSaved: (avatar: any) => void }) {
  const { user, setUser } = useAuth();
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState<any>(user.avatar);
  const [busy, setBusy] = useState(false);
  const file = useRef<HTMLInputElement>(null);
  const done = (av: any) => { setUser({ ...user, avatar: av }); onSaved(av); setOpen(false); toast.success("Profile picture updated"); };

  const save = async () => {
    setBusy(true);
    try { done((await api.put("/me/avatar", value)).data); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const upload = async (f?: File) => {
    if (!f) return;
    if (f.size > 5 * 1024 * 1024) return toast.error("Photos must be under 5 MB");
    const fd = new FormData();
    fd.append("file", f);
    setBusy(true);
    try { done((await api.post("/me/avatar/photo", fd)).data); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); if (file.current) file.current.value = ""; }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (o) setValue(user.avatar); }}>
      <DialogTrigger asChild>
        <button data-testid="change-avatar-button" className="group relative rounded-full" aria-label="Change profile picture">
          <Emblem avatar={user.avatar} size={88} />
          <span className="absolute -bottom-1 -right-1 flex h-8 w-8 items-center justify-center rounded-full bg-[#C86D51] text-white shadow-md transition-transform group-hover:scale-110"><Camera className="h-4 w-4" /></span>
        </button>
      </DialogTrigger>
      <DialogContent className="max-w-lg" data-testid="avatar-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl">Your profile picture</DialogTitle></DialogHeader>
        <div className="flex items-center gap-4 rounded-2xl bg-[#F3ECE0]/60 p-4">
          <Emblem avatar={value} size={64} />
          <div className="flex-1">
            <p className="text-sm font-semibold text-slate-800">Use your own photo</p>
            <p className="text-xs text-slate-500">JPG, PNG or WebP, up to 5 MB. Other members will see it.</p>
          </div>
          <input ref={file} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" data-testid="avatar-photo-input" onChange={(e) => upload(e.target.files?.[0])} />
          <Button size="sm" variant="outline" disabled={busy} data-testid="avatar-upload-button" onClick={() => file.current?.click()} className="rounded-full">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Upload"}
          </Button>
        </div>
        <p className="text-sm font-semibold text-slate-700">Or choose a picture</p>
        <EmblemPicker value={value} onChange={setValue} prefix="avatar" />
        <Button disabled={busy} onClick={save} data-testid="avatar-save-button" className="w-full rounded-full bg-[#1E293B]">Save picture</Button>
      </DialogContent>
    </Dialog>
  );
}
